import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Paintbrush, Home, Wrench, Zap, Grid3X3, Droplets, Square, DoorOpen, Hammer, ShieldCheck, Layers, PaintBucket, CircleDot } from "lucide-react";

const services = [
  {
    name: "Stucco",
    icon: Layers,
    description: "Expert stucco repair and installation for beautiful, durable exterior walls.",
    priceRange: "$8-$15 per sq ft"
  },
  {
    name: "Siding",
    icon: Home,
    description: "Vinyl, wood, and fiber cement siding installation and repair services.",
    priceRange: "$5-$12 per sq ft"
  },
  {
    name: "Soffit/Facia",
    icon: Square,
    description: "Professional soffit and fascia repair or complete replacement.",
    priceRange: "$6-$20 per linear ft"
  },
  {
    name: "Roof Repair",
    icon: ShieldCheck,
    description: "Minor roof repairs, shingle replacement, and leak fixes.",
    priceRange: "$300-$1500+"
  },
  {
    name: "Door Installation",
    icon: DoorOpen,
    description: "Interior and exterior door installation and replacement.",
    priceRange: "$150-$500 per door"
  },
  {
    name: "Window Installation",
    icon: Grid3X3,
    description: "Complete window replacement and new window installations.",
    priceRange: "$200-$800 per window"
  },
  {
    name: "Screen Installation",
    icon: Square,
    description: "Screen door and window screen installation and repair.",
    priceRange: "$50-$200 per screen"
  },
  {
    name: "Exterior Paint",
    icon: PaintBucket,
    description: "Complete exterior house painting with quality materials.",
    priceRange: "$1.50-$4 per sq ft"
  },
  {
    name: "Interior Paint",
    icon: Paintbrush,
    description: "Interior room painting, accent walls, and finishing.",
    priceRange: "$1-$3 per sq ft"
  },
  {
    name: "Drywall Installation",
    icon: Layers,
    description: "Drywall installation, repair, texturing, and finishing.",
    priceRange: "$2-$4 per sq ft"
  },
  {
    name: "Flooring",
    icon: Grid3X3,
    description: "Hardwood, laminate, and vinyl flooring installation.",
    priceRange: "$3-$12 per sq ft"
  },
  {
    name: "Tile",
    icon: CircleDot,
    description: "Floor and wall tile installation for kitchens and baths.",
    priceRange: "$5-$15 per sq ft"
  },
  {
    name: "Cabinet Installation",
    icon: Hammer,
    description: "Kitchen and bathroom cabinet installation and replacement.",
    priceRange: "$100-$500 per cabinet"
  },
  {
    name: "Baseboards",
    icon: Wrench,
    description: "Baseboard installation, replacement, and trim work.",
    priceRange: "$3-$8 per linear ft"
  },
  {
    name: "Minor Electrical",
    icon: Zap,
    description: "Light fixtures, outlets, switches, and ceiling fans.",
    priceRange: "$75-$200 per fixture"
  },
  {
    name: "And Much More",
    icon: Wrench,
    description: "Contact us for any home improvement needs not listed above.",
    priceRange: "Contact for estimate"
  }
];

const ServicesPage = () => {
  React.useEffect(() => { document.title = "Our Services | New Leaf PMC LLC"; }, []);
  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="services-page">
      {/* Header */}
      <section className="py-16 md:py-24 bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h1>
          <p className="text-[#A0AAB2] text-lg max-w-2xl mx-auto">
            From minor repairs to complete renovations, New Leaf PMC handles all your home improvement needs with quality craftsmanship.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div
                  key={idx}
                  className="group bg-[#15161C] border border-[#2A2B32] rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300"
                  data-testid={`service-card-${idx}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4A2B6E]/30 to-[#0D5C63]/30 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Icon size={28} className="text-[#FF6B35]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                      <p className="text-[#A0AAB2] text-sm mb-3">{service.description}</p>
                      <p className="text-[#FF6B35] text-sm font-medium">{service.priceRange}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#4A2B6E]/30 via-[#0D5C63]/30 to-[#FF6B35]/30 border-y border-[#2A2B32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-[#A0AAB2] mb-8 text-lg">
            Contact us today for a free estimate on your project.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/booking"
              className="inline-flex items-center justify-center space-x-2 gradient-btn px-8 py-4 rounded-lg font-bold"
              data-testid="services-book-btn"
            >
              <Calendar size={20} />
              <span>Book Free Estimate</span>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-[#15161C] border border-[#2A2B32] text-white px-8 py-4 rounded-lg font-bold hover:bg-white/5 transition-all"
              data-testid="services-contact-btn"
            >
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
